/**
 * Contacts de sûreté — délégués de secours désignés par un patient adulte, capables
 * d'authentifier une autorisation à sa place quand il est temporairement injoignable.
 */

import { apiFetch } from "./client";

export type StatutConfirmationContact = "non_soumis" | "en_attente" | "confirme" | "refuse";

export type ContactSurete = {
  id: string;
  patientNip: string;
  nom: string;
  email: string;
  telephone: string | null;
  relation: string;
  priorite: number;
  statutConfirmation: StatutConfirmationContact;
  dateConfirmation: string | null;
  dateCreation: string;
};

type ApiResult<T> = { ok: true; data: T } | { ok: false; message: string; errorCode?: string };

function unwrap<T>(payload: unknown): T {
  const p = payload as { data?: unknown } & Record<string, unknown>;
  return (p?.data ?? p) as T;
}

export async function listerContactsSurete(): Promise<ApiResult<ContactSurete[]>> {
  const out = await apiFetch<unknown>("/patient/patients/contacts-surete");
  if (!out.ok) return { ok: false, message: out.message, errorCode: out.errorCode };
  const data = unwrap<ContactSurete[]>(out.data);
  return { ok: true, data: Array.isArray(data) ? data : [] };
}

export async function ajouterContactSurete(body: {
  nom: string;
  email: string;
  telephone?: string;
  relation: string;
  priorite: number;
}): Promise<ApiResult<ContactSurete>> {
  const out = await apiFetch<unknown>("/patient/patients/contacts-surete", {
    method: "POST",
    body: JSON.stringify(body),
  });
  if (!out.ok) return { ok: false, message: out.message, errorCode: out.errorCode };
  return { ok: true, data: unwrap<ContactSurete>(out.data) };
}

export async function modifierContactSurete(
  id: string,
  body: Partial<{ nom: string; email: string; telephone: string; relation: string; priorite: number }>
): Promise<ApiResult<ContactSurete>> {
  const out = await apiFetch<unknown>(`/patient/patients/contacts-surete/${encodeURIComponent(id)}`, {
    method: "PUT",
    body: JSON.stringify(body),
  });
  if (!out.ok) return { ok: false, message: out.message, errorCode: out.errorCode };
  return { ok: true, data: unwrap<ContactSurete>(out.data) };
}

export async function supprimerContactSurete(id: string): Promise<ApiResult<{ deleted: boolean }>> {
  const out = await apiFetch<unknown>(`/patient/patients/contacts-surete/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
  if (!out.ok) return { ok: false, message: out.message, errorCode: out.errorCode };
  return { ok: true, data: unwrap<{ deleted: boolean }>(out.data) };
}

export async function renvoyerConfirmationContactSurete(id: string): Promise<ApiResult<ContactSurete>> {
  const out = await apiFetch<unknown>(`/patient/patients/contacts-surete/${encodeURIComponent(id)}/renvoyer-confirmation`, {
    method: "POST",
  });
  if (!out.ok) return { ok: false, message: out.message, errorCode: out.errorCode };
  return { ok: true, data: unwrap<ContactSurete>(out.data) };
}

/** Route publique — le contact n'a pas de compte Agbaza. */
export async function confirmerContactSurete(token: string): Promise<ApiResult<{ nom: string }>> {
  const out = await apiFetch<unknown>("/patient/patients/contacts-surete/confirmer", {
    method: "POST",
    body: JSON.stringify({ token }),
    skipAuth: true,
  });
  if (!out.ok) return { ok: false, message: out.message, errorCode: out.errorCode };
  return { ok: true, data: unwrap<{ nom: string }>(out.data) };
}

/** Déclenché côté professionnel/établissement, pas côté patient. */
export async function demanderAccesViaContactSurete(
  patientNip: string,
  contactSureteId: string,
  justification: string
): Promise<ApiResult<{ otpRequestId: string; contactNomMasque: string }>> {
  const out = await apiFetch<unknown>(`/patient/patients/${encodeURIComponent(patientNip)}/access/contact-surete`, {
    method: "POST",
    body: JSON.stringify({ contactSureteId, justification }),
  });
  if (!out.ok) return { ok: false, message: out.message, errorCode: out.errorCode };
  return { ok: true, data: unwrap<{ otpRequestId: string; contactNomMasque: string; devOtpCode?: string }>(out.data) };
}

export async function verifierAccesViaContactSurete(
  patientNip: string,
  otpRequestId: string,
  otpCode: string
): Promise<ApiResult<{ liaisonId: string }>> {
  const out = await apiFetch<unknown>(`/patient/patients/${encodeURIComponent(patientNip)}/access/contact-surete/verify`, {
    method: "POST",
    body: JSON.stringify({ otpRequestId, otpCode }),
  });
  if (!out.ok) return { ok: false, message: out.message, errorCode: out.errorCode };
  return { ok: true, data: unwrap<{ liaisonId: string }>(out.data) };
}
